import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { bootstrapSession } from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
import { useClientStore } from '../../stores/clientStore'
import { fetchCurrentUser, login, logout } from './api'

/**
 * Runs once per tab: if there's no in-memory access token, exchange the
 * HttpOnly refresh cookie for a fresh access token. Protected routes wait
 * on this before deciding to redirect to /login.
 */
export function useSession() {
  const token = useAuthStore((s) => s.accessToken)
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      if (token) return true
      return await bootstrapSession()
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}

export function useLogin() {
  const setAccessToken = useAuthStore((s) => s.setAccessToken)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      login(email, password),
    onSuccess: (tokens) => {
      setAccessToken(tokens.access_token)
      qc.setQueryData(['session'], true)
    },
  })
}

export function useCurrentUser() {
  const token = useAuthStore((s) => s.accessToken)
  return useQuery({
    queryKey: ['users', 'me'],
    queryFn: fetchCurrentUser,
    enabled: !!token,
  })
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear)
  const clearClient = useClientStore((s) => s.clear)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      clear()
      clearClient()
      queryClient.clear()
      queryClient.setQueryData(['session'], false)
    },
  })
}
